import { useNavigate } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";

const MODES = [
  {
    id: "smart",
    icon: "/version1.png",
    title: "Smart Mode",
    tagline: "AI builds the interview for you",
    time: "~2–3 min",
    route: "/org-admin/create-jd-smart",
    summary:
      "Add the role and pick an interview style. AI chooses the skills and questions, and you can add extra tests if needed.",
    bestFor: "High-volume hiring and getting a role live quickly",
  },
  {
    id: "custom",
    icon: "/version2.png",
    title: "Custom Mode",
    tagline: "You control every part of the round",
    time: "~8–10 min",
    route: "/org-admin/create-jd-custom",
    summary:
      "Add the role, then shape the interview round yourself: its length, the focus areas, and how hard each area should be.",
    bestFor: "Specialised or senior roles where depth matters",
  },
];

const JDSelectionPage = () => {
  const navigate = useNavigate();

  const Card = ({ icon, title, tagline, time, summary, bestFor, onClick }) => {
    return (
      <div
        className="dark:bg-white/5 border-gray-600/30 hover:border-blue-300 p-6 rounded-2xl w-full max-w-[340px] flex flex-col transition-all duration-300 hover:scale-105 hover:shadow-md cursor-pointer backdrop-blur-sm border relative group"
        onClick={onClick}
      >
        <div className="flex items-center gap-4 mb-4">
          <img src={icon} className="w-12 h-12" />
          <div>
            <p className="text-lg dark:text-[#C9A3FF] text-violet-600">{title}</p>
            <p className="text-xs font-semibold dark:text-[#CCCCCC] text-gray-700">{tagline}</p>
          </div>
        </div>

        <div className="inline-flex items-center gap-1.5 self-start text-xs px-2 py-1 mb-4 rounded-md border border-yellow-200 dark:border-yellow-200/30 bg-yellow-50/50 dark:bg-yellow-200/25 dark:text-[#f7dd88] text-textSecondary">
          <Clock size={12} />
          <span>Takes {time}</span>
        </div>

        <p className="text-[11px] uppercase tracking-wide mb-2 dark:text-gray-500 text-textSecondary">
          What you'll do
        </p>
        <p className="text-xs mb-4 flex-1 dark:text-[#CCCCCC] text-gray-700">{summary}</p>

        <p className="text-xs pt-3 border-t dark:border-gray-700/50 border-gray-200 dark:text-gray-400 text-textSecondary">
          <span className="font-medium dark:text-white text-textPrimary">Best for: </span>
          {bestFor}
        </p>
      </div>
    );
  };

  return (
    <div className="min-h-screen p-0 m-0 transition-colors">
      <div className="p-8 min-h-[90vh] border dark:bg-inherit bg-bgSecondary  dark:border-gray-700/50  rounded-2xl">
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 dark:text-white text-textSecondary hover:text-gray-300 transition-colors"
          >
            <ArrowLeft size={24} />
            <p className="text-lg font-medium dark:text-white text-textSecondary hover:text-gray-300 transition-colors">
              Back
            </p>
          </button>
        </div>
        <div className="text-center mb-10 pt-6">
          <p className="font-normal text-md dark:text-white text-textPrimary mb-2">
            Choose how you want to create the Job Description
          </p>
          <p className="text-xs dark:text-gray-400 text-textSecondary">
            Both modes create the same job and AI interview. They differ in how much you set up yourself.
          </p>
        </div>
        <div className="flex flex-wrap gap-10 justify-center items-stretch mb-10 max-w-4xl mx-auto">
          {MODES.map(({ id, route, ...mode }) => (
            <Card key={id} {...mode} onClick={() => navigate(route)} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default JDSelectionPage;
